import { useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  CloudUpload,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";

import { toTotal } from "@/lib/plannerPoints";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { renderNote } from "@/components/planner/TaskNoteDialog";
import { assigneeLabel, type PlannerTask } from "@/services/plannerService";

/** Labelled value in the expanded grid. Module-scope so React keeps its
 *  identity across renders (react-hooks/static-components). */
const Meta = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <span className="text-[10px] uppercase text-muted-foreground">{label}</span>
    <p className="text-foreground">{children}</p>
  </div>
);

/**
 * Parity pass 7: the upstream card view for < md. Collapsed it shows the
 * type, bucket, period, points and an inline status select; expanded it
 * adds qty / assignee / due / sync state, the Teamwork deep link, both
 * notes, and edit + two-step delete. One-Time keeps its red treatment.
 */
export function MobileTaskCard({
  task,
  selected,
  onSelectChange,
  onStatusChange,
  syncing = false,
  onSync,
  onEdit,
  onDelete,
}: {
  task: PlannerTask;
  selected: boolean;
  onSelectChange: () => void;
  onStatusChange: (status: "done" | "not_started") => void;
  /** A push of this row is in flight (E7): the Sync button is busy and disabled. */
  syncing?: boolean;
  onSync: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isOneTime = task.service_bucket === "One-Time";
  const done = task.status === "done";

  return (
    <div
      className={cn(
        "rounded-lg border p-3 transition-colors",
        isOneTime
          ? "border-destructive/30 bg-destructive/5"
          : "border-border bg-card",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <Checkbox
          checked={selected}
          onCheckedChange={onSelectChange}
          aria-label={`select ${task.task_type}`}
          className="mt-0.5 shrink-0"
        />
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          <p className="truncate text-sm font-medium text-foreground">{task.task_type}</p>
          <div className="mt-1 flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] font-normal">
              {task.service_bucket}
            </Badge>
            <span className="text-xs text-muted-foreground">{task.billing_period}</span>
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-sm font-semibold tabular-nums">{toTotal(task.points, task.qty)} pts</span>
          <Select
            value={done ? "Done" : "Pending"}
            onValueChange={(v) => onStatusChange(v === "Done" ? "done" : "not_started")}
          >
            <SelectTrigger
              className={cn(
                "h-7 w-[92px] border-0 text-xs font-medium",
                done ? "bg-teal-light text-teal-dark" : "bg-coral-light text-coral-dark",
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Done">Done</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {expanded ? (
        <div className="mt-3 space-y-2 border-t border-border pt-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <Meta label="Qty">{task.qty}</Meta>
            <Meta label="Assignee">{assigneeLabel(task.assignee)}</Meta>
            <Meta label="Due Date">{task.due_date || "—"}</Meta>
            <div>
              <span className="text-[10px] uppercase text-muted-foreground">Sync</span>
              <div className="flex items-center gap-1.5">
                {syncing ? (
                  <button
                    type="button"
                    disabled
                    aria-busy="true"
                    className="flex items-center gap-1 text-muted-foreground"
                  >
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span className="text-xs">Syncing…</span>
                  </button>
                ) : task.sync_status === "synced" ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-teal-dark" />
                    <span className="text-xs text-teal-dark">Synced</span>
                  </>
                ) : task.sync_status === "pending" ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    <span className="text-xs">Syncing…</span>
                  </>
                ) : task.sync_status === "failed" ? (
                  <button type="button" onClick={onSync} className="flex items-center gap-1 text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    <span className="text-xs">Retry</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onSync}
                    className="flex items-center gap-1 text-muted-foreground hover:text-teal-dark"
                  >
                    <CloudUpload className="h-3.5 w-3.5" />
                    <span className="text-xs">Sync</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {task.teamwork_task_id ? (
            <div>
              <span className="text-[10px] uppercase text-muted-foreground">Teamwork ID</span>
              <p>
                <a
                  href={`https://teamwork.agileandco.com/app/tasks/${task.teamwork_task_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-xs text-coral-dark hover:underline"
                >
                  {task.teamwork_task_id}
                </a>
              </p>
            </div>
          ) : null}

          {task.notes ? (
            <div>
              <span className="text-[10px] uppercase text-muted-foreground">Notes</span>
              <div className="text-xs text-foreground">{renderNote(task.notes)}</div>
            </div>
          ) : null}
          {task.internal_notes ? (
            <div>
              <span className="text-[10px] uppercase text-muted-foreground">Internal Notes</span>
              <div className="text-xs text-foreground">{renderNote(task.internal_notes)}</div>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              aria-label="edit"
              onClick={onEdit}
              className="text-muted-foreground hover:text-coral-dark"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            {confirmDelete ? (
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={onDelete} className="text-xs font-medium text-destructive">
                  Delete
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="text-xs text-muted-foreground">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                aria-label="delete"
                onClick={() => setConfirmDelete(true)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      ) : null}

      <div className="mt-1 flex justify-center">
        <button type="button" onClick={() => setExpanded((v) => !v)} aria-label={expanded ? "Collapse" : "Expand"}>
          <ChevronDown
            className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", expanded && "rotate-180")}
          />
        </button>
      </div>
    </div>
  );
}

export default MobileTaskCard;
